import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Divider, Flex, IconButton, Input, Select, Text } from '@chakra-ui/react';
import { WarningTwoIcon } from '@chakra-ui/icons';
import { toggleIsOpenCapturedTime } from '../../actions/captured-time';
import { ModalComponent } from '../../components/Modal/modal';
import { styles } from './captured-time.styles';
import { getDateOfGivenDayInWeek, formatDateStringSlashed } from '../utils/time-entry-utils';
import { ProtectedTimeList } from './protected-time-list';
import { BiomeIcon, IconName } from '@codametrix/verdigris';
const CapturedTime = (props)=>{
    const { listCapturedTime, listProtectedTime, deleteProtectedTimeEntry, updateProtectedTimeEntry, addProtectedTimeEntry, getMaxAllowedBackdate, listStatus, user, context } = props;
    const dispatch = useDispatch();
    const classes = styles();
    const dayOfWeekOrder = Object.freeze([
        'sunday',
        'monday',
        'tuesday',
        'wednesday',
        'thursday',
        'friday',
        'saturday'
    ]);
    let { capturedTimeWeek, protectedTimeWeek, maxAllowedBackdate, noPastCapturedTimeFlag, needRefreshFlag, addTimeFailed } = useSelector((state)=>state.capturedTime);
    let { statusList } = useSelector((state)=>state.myStatus);
    /** Fills empty `dateOfEntry` for display without mutating frozen Redux state. */ const capturedTimeWeekWithDates = useMemo(()=>capturedTimeWeek.map((day)=>day.dateOfEntry.length === 0 ? {
                ...day,
                dateOfEntry: getDateOfGivenDayInWeek(new Date(), day.day).toISOString().split('T')[0]
            } : day), [
        capturedTimeWeek
    ]);
    const [weeklyTotalHours, setWeeklyTotalHours] = useState(capturedTimeWeek.reduce((sum, current)=>sum + current.hoursWorked, 0));
    const [selectedDay, setSelectedDay] = useState(()=>capturedTimeWeekWithDates.find((capturedTimeDay)=>capturedTimeDay.day.toLowerCase() === dayOfWeekOrder[new Date().getDay()]));
    const [addTimeEntryFailed, setAddTimeEntryFailed] = useState(false);
    const [selectedDayOfWeek, setSelectedDayOfWeek] = useState(selectedDay?.day);
    const [weeksAgo, setWeeksAgo] = useState(0);
    const [backdateRemainder, setBackdateRemainder] = useState(+maxAllowedBackdate);
    const [protectedTimeWeekCurrent, setProtectedTimeWeekCurrent] = useState(protectedTimeWeek);
    const buildProtectedTimeDayDict = useCallback(()=>{
        // build and sort dictionary of protectedTime events for current selectedDay
        const updateDict = {};
        // console.log(protectedTimeWeekCurrent)
        // console.log(selectedDay)
        // console.log(selectedDayOfWeek)
        const currentDay = protectedTimeWeekCurrent?.filter((protectedTimeEntry)=>protectedTimeEntry.day.toLowerCase() === selectedDayOfWeek.toLowerCase()).sort((entry1, entry2)=>new Date(entry1.startTime).getTime() >= new Date(entry2.startTime).getTime() ? 1 : -1);
        for (const protectedTime of currentDay){
            updateDict[protectedTime.workLogId] = protectedTime;
        }
        return updateDict;
    }, [
        protectedTimeWeekCurrent,
        selectedDayOfWeek
    ]);
    const [protectedTimeDayDict, setProtectedTimeDayDict] = useState(buildProtectedTimeDayDict());
    const [disableBackWeek, setDisableBackWeek] = useState(noPastCapturedTimeFlag);
    const [newProtectedTime, setNewProtectedTime] = useState({
        workLogId: '',
        userUid: user.userId.toString(),
        tenantId: context.activeContext.tenantId.toString(),
        name: user.firstName + ' ' + user.lastName,
        day: '',
        startTime: '',
        endTime: '',
        duration: 0,
        reason: ''
    });
    const [dayEditDisabled, setDayEditDisabled] = useState({
        sunday: false,
        monday: false,
        tuesday: false,
        wednesday: false,
        thursday: false,
        friday: false,
        saturday: false
    });
    const fetchTimeForWeek = useCallback((previousSunday, nextSaturday)=>{
        const fetchCapturedTimeList = async ()=>{
            await listCapturedTime(previousSunday, nextSaturday, user, context);
        };
        // Adding one day to protectedTime call endTime in order to capture all entries on Saturday
        const fetchProtectedTimeList = async ()=>{
            await listProtectedTime(previousSunday, new Date(nextSaturday.getTime() + 24 * 60 * 60 * 1000), user, context);
        };
        fetchCapturedTimeList();
        fetchProtectedTimeList();
    }, [
        context,
        user,
        listCapturedTime,
        listProtectedTime
    ]);
    const refreshProtectedTime = useCallback(()=>{
        const searchDay = capturedTimeWeekWithDates.find((day)=>day.day.toLowerCase() === 'wednesday') ?? selectedDay;
        const sunday = getDateOfGivenDayInWeek(new Date(searchDay.dateOfEntry), 'Sunday');
        const saturday = getDateOfGivenDayInWeek(new Date(searchDay.dateOfEntry), 'Saturday');
        fetchTimeForWeek(sunday, saturday);
    }, [
        capturedTimeWeekWithDates,
        selectedDay,
        fetchTimeForWeek
    ]);
    useEffect(()=>{
        setProtectedTimeDayDict(buildProtectedTimeDayDict());
    }, [
        selectedDay,
        protectedTimeWeekCurrent,
        buildProtectedTimeDayDict
    ]);
    useEffect(()=>{
        setAddTimeEntryFailed(addTimeFailed);
    }, [
        addTimeFailed,
        setAddTimeEntryFailed
    ]);
    useEffect(()=>{
        if (needRefreshFlag) {
            refreshProtectedTime();
        }
    }, [
        needRefreshFlag,
        refreshProtectedTime
    ]);
    useEffect(()=>{
        setProtectedTimeWeekCurrent(protectedTimeWeek);
    }, [
        protectedTimeWeek
    ]);
    useEffect(()=>{
        // disable editing for days outside the range of the configured backdate maximum
        let backdateCounter = 7 - backdateRemainder;
        let newDayEditDisabled = dayEditDisabled;
        if (backdateRemainder < 7) {
            Object.keys(dayEditDisabled).forEach((day)=>{
                if (backdateCounter > 0) {
                    newDayEditDisabled[day] = true;
                    backdateCounter--;
                } else if (backdateCounter <= 0) {
                    newDayEditDisabled[day] = false;
                }
                if (backdateRemainder <= 0) {
                    newDayEditDisabled[day] = true;
                }
            });
        } else {
            Object.keys(dayEditDisabled).forEach((day)=>{
                newDayEditDisabled[day] = false;
            });
        }
        setDayEditDisabled(newDayEditDisabled);
    }, [
        backdateRemainder,
        dayEditDisabled
    ]);
    useEffect(()=>{
        // if no captured time exists for previous week, disable back button, return to last week
        if (noPastCapturedTimeFlag === true) {
            const sunday = getDateOfGivenDayInWeek(new Date(new Date(selectedDay.dateOfEntry).getTime()), 'Sunday');
            const saturday = getDateOfGivenDayInWeek(new Date(new Date(selectedDay.dateOfEntry).getTime()), 'Saturday');
            fetchTimeForWeek(sunday, saturday);
            if (weeksAgo > 0) {
                setWeeksAgo(weeksAgo - 1);
                setBackdateRemainder(backdateRemainder + 7);
            }
            setDisableBackWeek(true);
        }
    // eslint-disable-next-line
    }, [
        noPastCapturedTimeFlag
    ]);
    useEffect(()=>{
        const previousSunday = getDateOfGivenDayInWeek(new Date(), 'Sunday');
        const nextSaturday = getDateOfGivenDayInWeek(new Date(), 'Saturday');
        fetchTimeForWeek(previousSunday, nextSaturday);
        getMaxAllowedBackdate('max_allowed_days', context.activeContext?.tenantId);
    // eslint-disable-next-line
    }, []);
    useEffect(()=>{
        const fetchStatusList = async ()=>{
            await listStatus(context?.activeContext?.tenantId);
        };
        fetchStatusList();
    }, [
        listStatus,
        context
    ]);
    useEffect(()=>{
        selectedDay?.dateOfEntry.length > 0 ? setSelectedDay(capturedTimeWeekWithDates.find((capturedTimeDay)=>capturedTimeDay.day.toLowerCase() === selectedDay?.day.toLowerCase())) : setSelectedDay(capturedTimeWeekWithDates.find((capturedTimeDay)=>capturedTimeDay.day.toLowerCase() === dayOfWeekOrder[new Date().getDay()]));
    // eslint-disable-next-line
    }, [
        capturedTimeWeek,
        dayOfWeekOrder
    ]);
    useEffect(()=>{
        setWeeklyTotalHours(capturedTimeWeek.reduce((sum, current)=>sum + current.hoursWorked, 0));
    }, [
        capturedTimeWeek,
        protectedTimeWeek
    ]);
    useEffect(()=>{
        setSelectedDayOfWeek(selectedDay?.day);
    }, [
        selectedDay
    ]);
    const handleClose = ()=>{
        dispatch(toggleIsOpenCapturedTime(false));
        setAddTimeEntryFailed(false);
    };
    const handleDayClick = (day)=>{
        setSelectedDay(day);
    };
    const handleWeekBackClick = ()=>{
        const searchDay = capturedTimeWeekWithDates.find((day)=>day.day.toLowerCase() === 'wednesday') ?? selectedDay;
        const sunday = getDateOfGivenDayInWeek(new Date(new Date(searchDay.dateOfEntry).getTime() - 7 * 24 * 60 * 60 * 1000), 'Sunday');
        const saturday = getDateOfGivenDayInWeek(new Date(new Date(searchDay.dateOfEntry).getTime() - 7 * 24 * 60 * 60 * 1000), 'Saturday');
        fetchTimeForWeek(sunday, saturday);
        setWeeksAgo(weeksAgo + 1);
        setBackdateRemainder(backdateRemainder - 7);
    };
    const handleWeekForwardClick = ()=>{
        const searchDay = capturedTimeWeekWithDates.find((day)=>day.day.toLowerCase() === 'wednesday') ?? selectedDay;
        const sunday = getDateOfGivenDayInWeek(new Date(new Date(searchDay.dateOfEntry).getTime() + 7 * 24 * 60 * 60 * 1000), 'Sunday');
        const saturday = getDateOfGivenDayInWeek(new Date(new Date(searchDay.dateOfEntry).getTime() + 7 * 24 * 60 * 60 * 1000), 'Saturday');
        fetchTimeForWeek(sunday, saturday);
        setWeeksAgo(weeksAgo - 1);
        setBackdateRemainder(backdateRemainder + 7);
        setDisableBackWeek(false);
    };
    const handleUpdateProtectedTime = (protectedTime, event)=>{
        const { target } = event;
        const updatedProtectedTimeEntry = {
            ...protectedTime
        };
        if (target.id === 'startTime') {
            const updatedStartTime = new Date(selectedDay?.dateOfEntry + 'T12:00:00Z');
            updatedStartTime.setHours(+target.value.substring(0, 2), +target.value.substring(3, 5));
            updatedProtectedTimeEntry.startTime = updatedStartTime.toISOString().substring(0, 19) + 'Z';
        } else if (target.id === 'reason') {
            updatedProtectedTimeEntry.reason = target.value;
        }
        if (updatedProtectedTimeEntry.duration === null && updatedProtectedTimeEntry.endTime === null) {
            updatedProtectedTimeEntry.duration = 0;
        }
        setProtectedTimeDayDict({
            ...protectedTimeDayDict,
            [protectedTime.workLogId]: updatedProtectedTimeEntry
        });
        updateProtectedTimeEntry(protectedTime.workLogId.toString(), updatedProtectedTimeEntry);
    };
    const handleDeleteProtectedTime = (protectedTime)=>{
        deleteProtectedTimeEntry(protectedTime.workLogId);
    };
    const handleUpdateNewProtectedTimeEntry = (event)=>{
        const { target } = event;
        const updatedNewProtectedTimeEntry = {
            ...newProtectedTime
        };
        target.id === 'newStartTime' ? updatedNewProtectedTimeEntry.startTime = target.value : target.id === 'newReason' ? updatedNewProtectedTimeEntry.reason = target.value : updatedNewProtectedTimeEntry.duration = +target.value;
        setNewProtectedTime(updatedNewProtectedTimeEntry);
    };
    const handleAddProtectedTime = ()=>{
        const newStartTime = new Date(selectedDay?.dateOfEntry + 'T12:00:00Z');
        newStartTime.setHours(+newProtectedTime.startTime.substring(0, 2), +newProtectedTime.startTime.substring(3, 5));
        const protectedTimeToAdd = {
            ...newProtectedTime,
            day: selectedDayOfWeek,
            startTime: newStartTime.toISOString().substring(0, 19) + 'Z'
        };
        addProtectedTimeEntry(protectedTimeToAdd);
    };
    return /*#__PURE__*/ _jsx(ModalComponent, {
        size: 'm',
        isOpen: true,
        handleClose: handleClose,
        showCloseIcon: true,
        modalHeader: /*#__PURE__*/ _jsx(Flex, {
            sx: classes['.capturedTimeHeader'],
            children: "Captured Time"
        }),
        modalContent: /*#__PURE__*/ _jsxs(Flex, {
            direction: "column",
            children: [
                /*#__PURE__*/ _jsxs(Flex, {
                    direction: "row",
                    sx: classes['.capturedTimeSubheaderContainer'],
                    children: [
                        weeksAgo === 0 ? /*#__PURE__*/ _jsx(Text, {
                            sx: classes['.activityLabel'],
                            children: "This week's activity"
                        }) : weeksAgo === 1 ? /*#__PURE__*/ _jsx(Text, {
                            sx: classes['.activityLabel'],
                            children: "Last week's activity"
                        }) : /*#__PURE__*/ _jsxs(Text, {
                            sx: classes['.activityLabel'],
                            children: [
                                "Activity ",
                                weeksAgo,
                                " weeks ago"
                            ]
                        }),
                        /*#__PURE__*/ _jsxs(Text, {
                            sx: classes['.weeklyTotal'],
                            children: [
                                "Weekly Total: ",
                                weeklyTotalHours,
                                " hours"
                            ]
                        })
                    ]
                }),
                disableBackWeek ? /*#__PURE__*/ _jsx(Flex, {
                    sx: classes['.noPreviousTimeWarning'],
                    children: /*#__PURE__*/ _jsx(Text, {
                        children: "No time entered previous to this week!"
                    })
                }) : '',
                /*#__PURE__*/ _jsxs(Flex, {
                    direction: "column",
                    children: [
                        /*#__PURE__*/ _jsxs(Flex, {
                            direction: "row",
                            sx: classes['.weekTimelineContainer'],
                            children: [
                                /*#__PURE__*/ _jsx(IconButton, {
                                    variant: "outline",
                                    "aria-label": "back-button",
                                    onClick: handleWeekBackClick,
                                    disabled: disableBackWeek,
                                    sx: classes['.changeWeekButton'],
                                    icon: /*#__PURE__*/ _jsx(BiomeIcon, {
                                        iconName: IconName.Arrow_Left,
                                        titleText: "Left"
                                    })
                                }),
                                [
                                    ...capturedTimeWeekWithDates ?? []
                                ].sort((day1, day2)=>dayOfWeekOrder.indexOf(day1.day.toLowerCase()) - dayOfWeekOrder.indexOf(day2.day.toLowerCase())).map((capturedTimeDay)=>{
                                    return /*#__PURE__*/ _jsxs(Flex, {
                                        direction: "column",
                                        sx: selectedDayOfWeek === capturedTimeDay.day ? classes['.dayTimelineContainerSelected'] : classes['.dayTimelineContainer'],
                                        onClick: ()=>handleDayClick(capturedTimeDay),
                                        children: [
                                            /*#__PURE__*/ _jsx(Text, {
                                                sx: classes['.dayTimelineDayOfWeek'],
                                                children: capturedTimeDay.day
                                            }),
                                            /*#__PURE__*/ _jsx(Flex, {
                                                sx: classes['.dayTimelineDate'],
                                                children: capturedTimeDay.dateOfEntry.substring(8)
                                            }),
                                            selectedDayOfWeek === capturedTimeDay.day && /*#__PURE__*/ _jsx(Divider, {
                                                sx: classes['.dayTimelineDividerSelected']
                                            })
                                        ]
                                    });
                                }),
                                /*#__PURE__*/ _jsx(IconButton, {
                                    variant: "outline",
                                    "aria-label": "forward-button",
                                    onClick: handleWeekForwardClick,
                                    sx: classes['.changeWeekButton'],
                                    disabled: weeksAgo === 0,
                                    icon: /*#__PURE__*/ _jsx(BiomeIcon, {
                                        iconName: IconName.Arrow_Right,
                                        titleText: "Right"
                                    })
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(Divider, {
                            sx: classes['.dayTimelineDivider']
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs(Flex, {
                    direction: "row",
                    children: [
                        /*#__PURE__*/ _jsxs(Text, {
                            sx: classes['.selectedDayLabel'],
                            children: [
                                selectedDayOfWeek,
                                ",",
                                ' ',
                                selectedDay?.dateOfEntry.length > 0 ? formatDateStringSlashed(selectedDay?.dateOfEntry) : formatDateStringSlashed(new Date().toISOString().split('T')[0])
                            ]
                        }),
                        /*#__PURE__*/ _jsxs(Flex, {
                            sx: classes['.hoursLabelContainer'],
                            children: [
                                /*#__PURE__*/ _jsxs(Flex, {
                                    sx: classes['.expectedHoursLabel'],
                                    children: [
                                        /*#__PURE__*/ _jsx(Text, {
                                            sx: classes['.hoursLabel'],
                                            children: "Expected: "
                                        }),
                                        /*#__PURE__*/ _jsxs(Text, {
                                            sx: classes['.hoursText'],
                                            children: [
                                                selectedDay?.hoursWorked,
                                                " hours"
                                            ]
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs(Flex, {
                                    sx: classes['.totalHoursLabel'],
                                    children: [
                                        /*#__PURE__*/ _jsx(Text, {
                                            sx: classes['.hoursLabel'],
                                            children: "Total: "
                                        }),
                                        /*#__PURE__*/ _jsxs(Text, {
                                            sx: classes['.hoursText'],
                                            children: [
                                                protectedTimeWeekCurrent?.filter((protectedTimeEntry)=>protectedTimeEntry.day.toLowerCase() === selectedDayOfWeek.toLowerCase()).reduce((sum, current)=>sum + current.duration, 0).toFixed(2),
                                                ' ',
                                                "hours"
                                            ]
                                        })
                                    ]
                                })
                            ]
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs(Flex, {
                    direction: "column",
                    children: [
                        dayEditDisabled[selectedDayOfWeek.toLowerCase()] ? /*#__PURE__*/ _jsx(Flex, {
                            sx: classes['.noEditWarning'],
                            children: /*#__PURE__*/ _jsx(Text, {
                                children: "No edit access: beyond configured maximum backdate days."
                            })
                        }) : '',
                        /*#__PURE__*/ _jsx(ProtectedTimeList, {
                            dayEditDisabled: dayEditDisabled,
                            protectedTimeDayDict: protectedTimeDayDict,
                            handleUpdateProtectedTime: handleUpdateProtectedTime,
                            handleDeleteProtectedTime: handleDeleteProtectedTime,
                            setProtectedTimeDayDict: setProtectedTimeDayDict,
                            statusList: statusList
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs(Flex, {
                    direction: "row",
                    sx: classes['.addTimeRowContainer'],
                    children: [
                        /*#__PURE__*/ _jsx(Flex, {
                            sx: classes['.startTimeSelect'],
                            children: /*#__PURE__*/ _jsx(Input, {
                                type: 'time',
                                disabled: dayEditDisabled[selectedDayOfWeek.toLowerCase()],
                                id: "newStartTime",
                                placeholder: "Start time",
                                onChange: (e)=>handleUpdateNewProtectedTimeEntry(e)
                            })
                        }),
                        /*#__PURE__*/ _jsx(Flex, {
                            sx: classes['.descriptionSelectContainer'],
                            children: /*#__PURE__*/ _jsx(Select, {
                                placeholder: "Description",
                                disabled: dayEditDisabled[selectedDayOfWeek.toLowerCase()],
                                id: "newReason",
                                sx: classes['.descriptionSelect'],
                                onChange: (e)=>handleUpdateNewProtectedTimeEntry(e),
                                children: statusList.rowValues.map((status)=>{
                                    return /*#__PURE__*/ _jsx("option", {
                                        id: "statusReason",
                                        value: status.Reasons,
                                        children: status.Reasons
                                    });
                                })
                            })
                        }),
                        /*#__PURE__*/ _jsx(Flex, {
                            sx: {
                                ...classes['.enterHoursSelect'],
                                "input::placeholder": {
                                    color: "gray.400"
                                }
                            },
                            children: /*#__PURE__*/ _jsx(Input, {
                                type: 'number',
                                disabled: dayEditDisabled[selectedDayOfWeek.toLowerCase()],
                                id: "newDuration",
                                placeholder: "Enter hours",
                                _placeholder: {
                                    color: 'gray.300'
                                },
                                onChange: (e)=>handleUpdateNewProtectedTimeEntry(e)
                            })
                        }),
                        /*#__PURE__*/ _jsx(Button, {
                            sx: classes['.addTimeButton'],
                            disabled: dayEditDisabled[selectedDayOfWeek.toLowerCase()],
                            onClick: handleAddProtectedTime,
                            children: "Add time"
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(ModalComponent, {
                    size: 's',
                    isOpen: addTimeEntryFailed,
                    handleClose: ()=>setAddTimeEntryFailed(false),
                    showCloseIcon: true,
                    modalHeader: /*#__PURE__*/ _jsxs(Flex, {
                        sx: classes['.timeEntryOverlapErrorHeader'],
                        children: [
                            /*#__PURE__*/ _jsx(WarningTwoIcon, {}),
                            " Time Entry Error"
                        ]
                    }),
                    modalContent: /*#__PURE__*/ _jsx("div", {
                        children: "The time periods you have entered overlap, please correct your time entry and try again."
                    })
                })
            ]
        })
    });
};
export { CapturedTime };
